<template>
  <div class="bg-background text-foreground min-h-screen" :class="isEditing ? 'pb-20' : 'pb-10'">
    <div class="w-full px-4 sm:px-6 py-5">

      <!-- Page header -->
      <div class="flex items-start justify-between gap-4 mb-5">
        <div class="flex items-center gap-3 min-w-0">
          <!-- Avatar -->
          <div class="w-14 h-14 rounded-full bg-primary/10 border border-border flex items-center justify-center flex-shrink-0 overflow-hidden">
            <img
              v-if="profile.profile_picture"
              :src="profile.profile_picture"
              :alt="userInitials"
              class="w-full h-full object-cover"
              @error="handleImageError"
            />
            <span v-else class="text-xl font-bold text-primary">{{ userInitials }}</span>
          </div>
          <div class="min-w-0">
            <h1 class="text-xl font-bold leading-tight truncate">
              {{ profile.title || 'Complete Your Profile' }}
            </h1>
            <p class="text-sm text-muted-foreground leading-snug">
              {{ profile.location || 'Add your location' }}
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2 flex-shrink-0">
          <button
            v-if="!isEditing"
            @click="startEditing"
            class="inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold shadow-sm"
          >
            <i class="fas fa-pen text-xs"></i> Edit Profile
          </button>
        </div>
      </div>

      <!-- Two-column layout -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-5">

        <!-- Left column -->
        <div class="lg:col-span-2 space-y-5">

          <!-- About section -->
          <section class="bg-card border border-border rounded-xl">
            <div class="px-5 py-3.5 border-b border-border flex items-center justify-between">
              <span class="text-sm font-bold">About</span>
            </div>
            <div class="px-5 py-4">
              <!-- View mode -->
              <template v-if="!isEditing">
                <p v-if="profile.about" class="text-sm text-muted-foreground leading-relaxed">{{ profile.about }}</p>
                <p v-else class="text-sm text-muted-foreground italic">No description added yet.</p>
              </template>
              <!-- Edit mode -->
              <template v-else>
                <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">Bio / About</label>
                <textarea
                  v-model="editedProfile.about"
                  rows="5"
                  class="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                  placeholder="Tell employers about yourself, your experience, and what you're looking for"
                ></textarea>
              </template>
            </div>
          </section>

          <!-- Skills section -->
          <section class="bg-card border border-border rounded-xl">
            <div class="px-5 py-3.5 border-b border-border flex items-center justify-between">
              <span class="text-sm font-bold">Skills</span>
            </div>
            <div class="px-5 py-4">
              <!-- View mode -->
              <template v-if="!isEditing">
                <div v-if="profile.skills && profile.skills.length > 0" class="flex flex-wrap gap-1.5">
                  <span
                    v-for="skill in profile.skills"
                    :key="skill"
                    class="inline-flex items-center text-[11px] font-medium px-2.5 py-0.5 rounded-full bg-primary/10 text-primary"
                  >
                    {{ skill }}
                  </span>
                </div>
                <p class="text-sm text-muted-foreground italic" v-else>No skills added yet.</p>
              </template>
              <!-- Edit mode -->
              <template v-else>
                <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">Your Skills</label>
                <div class="relative mb-3">
                  <input
                    v-model="skillInput"
                    type="text"
                    class="w-full h-10 px-3 pr-10 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40"
                    placeholder="Add a skill (e.g. JavaScript, Project Management)"
                    @keyup.enter="addSkill"
                  />
                  <button
                    @click="addSkill"
                    class="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded text-primary hover:bg-primary/10 transition"
                  >
                    <i class="fas fa-plus text-xs"></i>
                  </button>
                </div>

                <div v-if="editedProfile.skills.length > 0" class="flex flex-wrap gap-1.5 mb-4">
                  <span
                    v-for="(skill, index) in editedProfile.skills"
                    :key="index"
                    class="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded bg-primary/10 text-primary border border-primary/20"
                  >
                    {{ skill }}
                    <button @click="removeSkill(index)" class="hover:text-red-500 transition-colors ml-0.5">
                      <i class="fas fa-times text-[9px]"></i>
                    </button>
                  </span>
                </div>

                <div>
                  <div class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Popular Skills</div>
                  <div class="flex flex-wrap gap-1.5">
                    <button
                      v-for="skill in popularSkills"
                      :key="skill"
                      @click="addPopularSkill(skill)"
                      class="h-7 px-2.5 rounded-lg border border-border bg-card text-xs font-medium hover:border-primary/40 hover:bg-primary/5 transition disabled:opacity-40 disabled:cursor-not-allowed"
                      :disabled="editedProfile.skills.includes(skill)"
                    >
                      + {{ skill }}
                    </button>
                  </div>
                </div>
              </template>
            </div>
          </section>

          <!-- Work Experience section -->
          <section class="bg-card border border-border rounded-xl">
            <div class="px-5 py-3.5 border-b border-border flex items-center justify-between">
              <span class="text-sm font-bold">Work Experience</span>
              <button
                v-if="isEditing"
                @click="addExperience"
                class="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg border border-border bg-card hover:bg-muted text-xs font-medium text-muted-foreground"
              >
                <i class="fas fa-plus text-[10px]"></i> Add experience
              </button>
            </div>
            <div class="px-5 py-4">
              <!-- View mode -->
              <template v-if="!isEditing">
                <div v-if="profile.experience && profile.experience.length > 0" class="space-y-4">
                  <div
                    v-for="(entry, index) in profile.experience"
                    :key="index"
                    class="border-b border-border last:border-0 pb-4 last:pb-0"
                  >
                    <div class="text-sm font-semibold text-foreground">{{ entry.title }}</div>
                    <p v-if="entry.company" class="text-sm text-muted-foreground mt-0.5">{{ entry.company }}</p>
                    <p v-if="entry.start_date" class="text-[11px] text-muted-foreground mt-0.5">{{ formatDateRange(entry.start_date, entry.end_date) }}</p>
                    <p class="text-sm text-muted-foreground mt-1 leading-relaxed">{{ entry.description }}</p>
                    <div v-if="entry.tools && entry.tools.length" class="flex flex-wrap gap-1.5 mt-2">
                      <span v-for="tool in entry.tools" :key="tool" class="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-muted text-foreground">{{ tool }}</span>
                    </div>
                    <p v-if="entry.highlights" class="text-sm text-foreground mt-2 leading-relaxed"><span class="font-semibold">Results:</span> {{ entry.highlights }}</p>
                  </div>
                </div>
                <p class="text-sm text-muted-foreground italic" v-else>No experience added yet.</p>
                <ExperienceStoriesList class="mt-6" />
              </template>
              <!-- Edit mode -->
              <template v-else>
                <div v-if="editedProfile.experience.length > 0" class="space-y-4">
                  <div
                    v-for="(entry, index) in editedProfile.experience"
                    :key="index"
                    class="rounded-lg border border-border p-4 space-y-3"
                  >
                    <div class="flex items-center justify-between">
                      <span class="text-xs font-bold text-muted-foreground uppercase tracking-wider">Experience {{ index + 1 }}</span>
                      <button @click="removeExperience(index)" class="h-6 w-6 flex items-center justify-center rounded text-muted-foreground hover:text-red-500 hover:bg-red-50 transition text-xs">
                        <i class="fas fa-times"></i>
                      </button>
                    </div>
                    <div>
                      <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">Job Title</label>
                      <input
                        v-model="entry.title"
                        type="text"
                        class="w-full h-10 px-3 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40"
                        placeholder="e.g. Virtual Assistant"
                      />
                    </div>
                    <div>
                      <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">Company</label>
                      <input
                        v-model="entry.company"
                        type="text"
                        class="w-full h-10 px-3 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40"
                        placeholder="Company name"
                      />
                    </div>
                    <div class="grid grid-cols-2 gap-3">
                      <div class="min-w-0">
                        <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">Start</label>
                        <input
                          :value="toMonth(entry.start_date)"
                          @input="entry.start_date = toDate($event.target.value)"
                          type="month"
                          class="w-full h-10 px-3 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                      </div>
                      <div class="min-w-0">
                        <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">End</label>
                        <input
                          :value="toMonth(entry.end_date)"
                          @input="entry.end_date = toDate($event.target.value)"
                          type="month"
                          class="w-full h-10 px-3 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                        <p class="text-[10px] text-muted-foreground mt-1">Blank if this is your current role</p>
                      </div>
                    </div>
                    <div>
                      <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">Description</label>
                      <textarea
                        v-model="entry.description"
                        rows="3"
                        class="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                        placeholder="Key responsibilities or achievements"
                      ></textarea>
                    </div>
                    <div>
                      <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">Tools used <span class="normal-case font-medium tracking-normal">(optional)</span></label>
                      <input
                        :value="(entry.tools || []).join(', ')"
                        @change="entry.tools = parseTools($event.target.value)"
                        type="text"
                        class="w-full h-10 px-3 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40"
                        placeholder="e.g. Asana, HubSpot, Canva (separate with commas)"
                      />
                    </div>
                    <div>
                      <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">Key results <span class="normal-case font-medium tracking-normal">(optional)</span></label>
                      <textarea
                        v-model="entry.highlights"
                        rows="2"
                        class="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                        placeholder="e.g. Cut email response time from 24h to 2h"
                      ></textarea>
                    </div>
                  </div>
                  <p class="text-[11px] text-muted-foreground"><i class="fas fa-wand-magic-sparkles mr-1"></i>Tools and results help your cover letters mention the right experience for each job.</p>
                </div>
                <div v-else class="text-center py-6">
                  <div class="text-sm text-muted-foreground mb-3">No experience added yet.</div>
                  <button
                    @click="addExperience"
                    class="inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-dashed border-border hover:border-primary/40 hover:bg-primary/5 text-sm font-medium text-muted-foreground transition"
                  >
                    <i class="fas fa-plus text-xs"></i> Add your first experience
                  </button>
                </div>
              </template>
            </div>
          </section>

          <!-- Portfolio section -->
          <section class="bg-card border border-border rounded-xl">
            <div class="px-5 py-3.5 border-b border-border flex items-center justify-between">
              <span class="text-sm font-bold">Portfolio</span>
              <button
                v-if="isEditing"
                @click="addProject"
                class="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg border border-border bg-card hover:bg-muted text-xs font-medium text-muted-foreground"
              >
                <i class="fas fa-plus text-[10px]"></i> Add project
              </button>
            </div>
            <div class="px-5 py-4">
              <!-- View mode -->
              <template v-if="!isEditing">
                <div v-if="profile.projects && profile.projects.length > 0" class="space-y-4">
                  <div
                    v-for="(project, index) in profile.projects"
                    :key="index"
                    class="border-b border-border last:border-0 pb-4 last:pb-0"
                  >
                    <div class="text-sm font-semibold text-foreground">{{ project.title }}</div>
                    <p v-if="project.role" class="text-sm text-muted-foreground mt-0.5">{{ project.role }}</p>
                    <p v-if="project.description" class="text-sm text-muted-foreground mt-1 leading-relaxed">{{ project.description }}</p>
                    <div v-if="project.tools && project.tools.length" class="flex flex-wrap gap-1.5 mt-2">
                      <span v-for="tool in project.tools" :key="tool" class="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-muted text-foreground">{{ tool }}</span>
                    </div>
                    <p v-if="project.highlights" class="text-sm text-foreground mt-2 leading-relaxed"><span class="font-semibold">Results:</span> {{ project.highlights }}</p>
                    <a v-if="project.url" :href="project.url" target="_blank" rel="noopener" class="text-xs text-primary hover:underline mt-1 inline-block break-all">{{ project.url }}</a>
                  </div>
                </div>
                <p class="text-sm text-muted-foreground italic" v-else>No portfolio projects added yet.</p>
              </template>
              <!-- Edit mode -->
              <template v-else>
                <div v-if="editedProfile.projects.length > 0" class="space-y-4">
                  <div
                    v-for="(project, index) in editedProfile.projects"
                    :key="index"
                    class="rounded-lg border border-border p-4 space-y-3"
                  >
                    <div class="flex items-center justify-between">
                      <span class="text-xs font-bold text-muted-foreground uppercase tracking-wider">Project {{ index + 1 }}</span>
                      <button @click="removeProject(index)" class="h-6 w-6 flex items-center justify-center rounded text-muted-foreground hover:text-red-500 hover:bg-red-50 transition text-xs" aria-label="Remove project">
                        <i class="fas fa-times"></i>
                      </button>
                    </div>
                    <div>
                      <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">Project name</label>
                      <input v-model="project.title" type="text" class="w-full h-10 px-3 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40" placeholder="e.g. Shopify store redesign" />
                    </div>
                    <div>
                      <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">Description</label>
                      <textarea
                        v-model="project.description"
                        rows="3"
                        class="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                        placeholder="What you built and the result"
                      ></textarea>
                    </div>
                    <div>
                      <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">Link</label>
                      <input v-model="project.url" type="url" class="w-full h-10 px-3 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40" placeholder="https://link-to-the-project.com" />
                    </div>
                    <div>
                      <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">Your role <span class="normal-case font-medium tracking-normal">(optional)</span></label>
                      <input v-model="project.role" type="text" class="w-full h-10 px-3 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40" placeholder="e.g. Built the whole store solo" />
                    </div>
                    <div>
                      <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">Tools / stack <span class="normal-case font-medium tracking-normal">(optional)</span></label>
                      <input
                        :value="(project.tools || []).join(', ')"
                        @change="project.tools = parseTools($event.target.value)"
                        type="text"
                        class="w-full h-10 px-3 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40"
                        placeholder="e.g. Shopify, Figma, Klaviyo (separate with commas)"
                      />
                    </div>
                    <div>
                      <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">Results <span class="normal-case font-medium tracking-normal">(optional)</span></label>
                      <textarea
                        v-model="project.highlights"
                        rows="2"
                        class="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                        placeholder="e.g. Sales up 25% in the first month"
                      ></textarea>
                    </div>
                  </div>
                  <p class="text-[11px] text-muted-foreground"><i class="fas fa-wand-magic-sparkles mr-1"></i>Helps your cover letters mention the right projects for each job.</p>
                </div>
                <div v-else class="text-center py-6">
                  <div class="text-sm text-muted-foreground mb-3">No portfolio projects added yet.</div>
                  <button
                    @click="addProject"
                    class="inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-dashed border-border hover:border-primary/40 hover:bg-primary/5 text-sm font-medium text-muted-foreground transition"
                  >
                    <i class="fas fa-plus text-xs"></i> Add your first project
                  </button>
                </div>
              </template>
            </div>
          </section>

        </div>

        <!-- Right column -->
        <div class="space-y-5">

          <!-- Profile Completeness -->
          <div class="bg-card border border-border rounded-xl px-5 py-4">
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm font-semibold">Profile completeness</span>
              <span class="text-sm font-bold text-primary">{{ completeness }}%</span>
            </div>
            <div class="h-2 rounded-full bg-muted">
              <div class="h-2 rounded-full bg-primary transition-all duration-500" :style="{ width: completeness + '%' }"></div>
            </div>
            <p v-if="completeness < 100" class="text-[11px] text-muted-foreground mt-2">
              Complete your profile to appear in more searches.
            </p>
          </div>

          <!-- Upwork profile (website / GitHub are kept on the profile but no longer shown) -->
          <section class="bg-card border border-border rounded-xl">
            <div class="px-5 py-3.5 border-b border-border flex items-center gap-2">
              <img :src="upworkLogo" alt="" class="h-5 w-5 rounded" />
              <span class="text-sm font-bold">Upwork profile</span>
            </div>
            <!-- View mode -->
            <template v-if="!isEditing">
              <div class="px-5 py-4 space-y-3">
                <a
                  v-if="profile.upwork_profile_url"
                  :href="profile.upwork_profile_url"
                  target="_blank"
                  rel="noopener"
                  class="text-sm text-primary hover:underline break-all"
                >{{ profile.upwork_profile_url }}</a>
                <p v-else class="text-sm text-muted-foreground italic">No Upwork profile linked yet.</p>

                <div v-if="hasUpworkStats" class="grid grid-cols-2 gap-2 pt-1">
                  <div v-for="stat in upworkStatList" :key="stat.label" class="rounded-lg bg-muted/40 px-3 py-2">
                    <div class="text-sm font-semibold text-foreground">{{ stat.value }}</div>
                    <div class="text-[11px] text-muted-foreground">{{ stat.label }}</div>
                  </div>
                </div>
              </div>
            </template>
            <!-- Edit mode -->
            <template v-else>
              <div class="px-5 py-4">
                <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">Profile URL</label>
                <input
                  v-model="editedProfile.upwork_profile_url"
                  type="url"
                  class="w-full h-10 px-3 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40"
                  placeholder="https://www.upwork.com/freelancers/~..."
                />
              </div>
            </template>
          </section>

          <!-- Resume section -->
          <section class="bg-card border border-border rounded-xl">
            <div class="px-5 py-3.5 border-b border-border">
              <span class="text-sm font-bold">Resume</span>
            </div>
            <!-- View mode -->
            <template v-if="!isEditing">
              <div class="px-5 py-4 space-y-2">
                <div v-if="profile.resume_url" class="flex items-center gap-3">
                  <div class="w-7 h-7 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-link text-muted-foreground text-xs"></i>
                  </div>
                  <a :href="profile.resume_url" target="_blank" class="text-sm text-primary hover:underline">Online Resume Link</a>
                </div>
                <div v-if="profile.resume" class="flex items-center gap-3">
                  <div class="w-7 h-7 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                    <i :class="getResumeIcon(profile.resume.name)" class="text-xs"></i>
                  </div>
                  <span class="text-sm text-foreground truncate flex-1" :title="profile.resume.name">{{ profile.resume.name }}</span>
                  <button
                    @click="downloadResume"
                    class="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-primary hover:bg-muted transition flex-shrink-0"
                    title="Download Resume"
                  >
                    <i class="fas fa-download text-xs"></i>
                  </button>
                </div>
                <p class="text-sm text-muted-foreground italic" v-if="!profile.resume_url && !profile.resume">
                  No resume added yet.
                </p>
              </div>
            </template>
            <!-- Edit mode -->
            <template v-else>
              <div class="px-5 py-4 space-y-4">
                <div>
                  <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">Resume URL</label>
                  <div class="relative">
                    <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <i class="fas fa-link text-muted-foreground text-xs"></i>
                    </span>
                    <input
                      v-model="editedProfile.resume_url"
                      type="url"
                      class="w-full h-10 pl-9 pr-3 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40"
                      placeholder="Link to your online resume"
                    />
                  </div>
                </div>

                <div>
                  <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">Upload Resume</label>
                  <div class="rounded-lg border border-dashed border-border">
                    <input
                      id="resume-upload"
                      type="file"
                      class="hidden"
                      accept=".pdf,.doc,.docx"
                      @change="handleResumeUpload"
                      ref="fileInput"
                    />

                    <div v-if="!editedProfile.resume && !editedProfile.newResume" class="text-center py-6 px-4">
                      <label for="resume-upload" class="cursor-pointer block hover:bg-muted/30 rounded-lg transition-colors p-2">
                        <i class="fas fa-cloud-upload-alt text-2xl text-muted-foreground mb-2"></i>
                        <p class="text-sm font-medium text-foreground">Drag and drop or click to browse</p>
                        <p class="text-xs text-muted-foreground mt-0.5">PDF, DOC, DOCX — max 5MB</p>
                      </label>
                    </div>

                    <div v-else class="flex items-center gap-3 p-3 bg-primary/5 rounded-lg m-1">
                      <i :class="getResumeIcon(editedProfile.newResume?.name || editedProfile.resume?.name)" class="text-xl flex-shrink-0"></i>
                      <span class="text-sm font-medium text-foreground truncate flex-1">
                        {{ editedProfile.newResume?.name || editedProfile.resume?.name || 'Current Resume' }}
                      </span>
                      <button
                        @click="removeResume"
                        class="h-6 w-6 flex items-center justify-center rounded text-muted-foreground hover:text-red-500 transition flex-shrink-0"
                        title="Remove Resume"
                      >
                        <i class="fas fa-times text-xs"></i>
                      </button>
                    </div>
                  </div>

                  <!-- Scanning indicator -->
                  <div v-if="resumeParsing && editedProfile.newResume" class="mt-3 rounded-lg border border-sky-200 bg-sky-50 p-3 flex items-start gap-2">
                    <i class="fas fa-circle-notch animate-spin text-sky-600 text-sm mt-0.5 flex-shrink-0"></i>
                    <div class="text-[12px] text-sky-900">Scanning resume and extracting profile details...</div>
                  </div>

                  <!-- Autofill summary -->
                  <div v-if="!resumeParsing && resumeAutofillFields.length > 0" class="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-start gap-2">
                    <i class="fas fa-circle-check text-emerald-600 text-sm mt-0.5 flex-shrink-0"></i>
                    <div class="text-[12px] text-emerald-900">
                      <span class="font-semibold">Resume scanned</span> — pre-filled:
                      <span class="font-medium">{{ resumeAutofillFields.join(', ') }}</span>
                    </div>
                  </div>

                  <!-- Scan failed -->
                  <div v-if="!resumeParsing && resumeScanFailed" class="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
                    <i class="fas fa-exclamation-triangle text-amber-600 text-sm mt-0.5 flex-shrink-0"></i>
                    <div class="text-[12px] text-amber-900">Couldn't scan this file — you can still save it and fill your profile manually.</div>
                  </div>
                </div>
              </div>
            </template>
          </section>

          <!-- Work details (years of experience is worked out from the work
               experience dates; remote/relocate are kept but no longer shown) -->
          <section class="bg-card border border-border rounded-xl">
            <div class="px-5 py-3.5 border-b border-border">
              <span class="text-sm font-bold">Work details</span>
            </div>
            <!-- View mode -->
            <template v-if="!isEditing">
              <div
                v-for="row in workDetailRows"
                :key="row.icon"
                class="flex items-center gap-3 px-5 py-3.5 border-b border-border last:border-0 hover:bg-muted/30 transition"
              >
                <div class="w-7 h-7 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                  <i :class="['fas', row.icon, 'text-muted-foreground text-xs']"></i>
                </div>
                <span class="text-sm" :class="row.value ? 'text-foreground' : 'text-muted-foreground italic'">{{ row.value || row.empty }}</span>
              </div>
            </template>
            <!-- Edit mode -->
            <template v-else>
              <div class="px-5 py-4 space-y-4">
                <div>
                  <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">Location</label>
                  <input v-model="editedProfile.location" type="text" class="w-full h-10 px-3 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40" placeholder="e.g. Manila, Philippines" />
                </div>
                <div>
                  <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">Hourly rate (USD)</label>
                  <input v-model.number="editedProfile.hourly_rate" type="number" min="0" step="0.5" class="w-full h-10 px-3 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40" placeholder="8.00" />
                </div>
                <div>
                  <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">Hours per week</label>
                  <select v-model="editedProfile.hours_per_week" class="w-full h-10 px-3 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40">
                    <option value="">Select</option>
                    <option v-for="opt in HOURS_PER_WEEK_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                  </select>
                </div>
                <div>
                  <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">English level</label>
                  <select v-model="editedProfile.english_level" class="w-full h-10 px-3 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40">
                    <option value="">Select</option>
                    <option v-for="opt in ENGLISH_LEVEL_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                  </select>
                </div>
                <label class="flex items-center gap-3 cursor-pointer">
                  <input
                    v-model="editedProfile.open_to_contract_to_hire"
                    type="checkbox"
                    class="h-4 w-4 rounded border-border text-primary focus:ring-primary accent-primary"
                  />
                  <span class="text-sm text-foreground">Open to contract-to-hire</span>
                </label>
              </div>
            </template>
          </section>

          <!-- Job Preferences section -->
          <section class="bg-card border border-border rounded-xl">
            <div class="px-5 py-3.5 border-b border-border">
              <span class="text-sm font-bold">Job Preferences</span>
            </div>
            <!-- View mode -->
            <template v-if="!isEditing">
              <div class="px-5 py-4 space-y-3">
                <div v-if="profile.job_preferences && profile.job_preferences.job_types && profile.job_preferences.job_types.length > 0">
                  <div class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Job Types</div>
                  <div class="flex flex-wrap gap-1.5">
                    <span
                      v-for="type in profile.job_preferences.job_types"
                      :key="type"
                      class="inline-flex items-center text-[11px] font-medium px-2.5 py-0.5 rounded-full bg-muted text-foreground"
                    >
                      {{ formatJobType(type) }}
                    </span>
                  </div>
                </div>

                <p class="text-sm text-muted-foreground italic" v-if="!profile.job_preferences ||
                  !profile.job_preferences.job_types || !profile.job_preferences.job_types.length">
                  No job preferences specified yet.
                </p>
              </div>
            </template>
            <!-- Edit mode -->
            <template v-else>
              <div class="px-5 py-4 space-y-5">

                <!-- Job Types -->
                <div>
                  <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2 block">Job Types</label>
                  <div class="space-y-2">
                    <label v-for="jt in [
                      { value: 'full-time', label: 'Full-time' },
                      { value: 'part-time', label: 'Part-time' },
                      { value: 'contract', label: 'Contract' },
                      { value: 'freelance', label: 'Freelance' },
                      { value: 'internship', label: 'Internship' },
                    ]" :key="jt.value" class="flex items-center gap-3 cursor-pointer">
                      <input
                        v-model="editedProfile.job_preferences.job_types"
                        :value="jt.value"
                        type="checkbox"
                        class="h-4 w-4 rounded border-border text-primary focus:ring-primary accent-primary"
                      />
                      <span class="text-sm text-foreground">{{ jt.label }}</span>
                    </label>
                  </div>
                </div>

              </div>
            </template>
          </section>

          <!-- Share Profile card (folded from header URL bar) -->
          <section class="bg-card border border-border rounded-xl">
            <div class="px-5 py-3.5 border-b border-border">
              <span class="text-sm font-bold">Share Profile</span>
            </div>
            <div class="px-5 py-4 space-y-3">
              <div v-if="!canShareProfile" class="rounded-lg border border-sky-200 bg-sky-50 p-3 flex items-start gap-2">
                <i class="fas fa-circle-info text-sky-600 text-sm mt-0.5 flex-shrink-0"></i>
                <div class="text-[12px] text-sky-900">
                  Your profile is set to <strong>{{ privacySettings.profile_visibility }}</strong>.
                  <router-link to="/settings" class="text-primary font-semibold hover:underline ml-1">Update privacy settings</router-link> to make it public and shareable.
                </div>
              </div>
              <div class="flex items-center gap-2">
                <input
                  type="text"
                  :value="publicProfileUrl"
                  readonly
                  class="flex-1 h-9 px-3 rounded-lg border border-border bg-muted/30 text-xs text-muted-foreground focus:outline-none truncate"
                />
                <button
                  v-if="!canShareProfile"
                  @click="makeProfilePublic"
                  :disabled="makingPublic"
                  class="h-9 px-3 rounded-lg border border-border bg-card hover:bg-muted text-xs font-medium text-muted-foreground whitespace-nowrap transition disabled:opacity-60"
                >
                  <i class="fas fa-globe mr-1 text-[10px]"></i>{{ makingPublic ? 'Updating...' : 'Make Public' }}
                </button>
                <button
                  @click="copyProfileUrl"
                  :disabled="!canShareProfile"
                  :title="canShareProfile ? 'Copy profile URL' : 'Set profile to public first'"
                  class="h-9 px-3 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-semibold whitespace-nowrap transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i class="fas" :class="copied ? 'fa-check' : 'fa-copy'"></i>
                  {{ copied ? 'Copied!' : 'Copy' }}
                </button>
              </div>
            </div>
          </section>

        </div>
      </div>
    </div>

    <!-- Sticky Save/Cancel Bar -->
    <div v-if="isEditing" class="fixed bottom-0 left-0 right-0 z-[60] bg-card border-t border-border px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] flex items-center justify-between">
      <p class="text-sm text-muted-foreground hidden sm:block">
        <i class="fas fa-pen-to-square mr-1.5 text-primary text-xs"></i> Editing profile...
      </p>
      <div class="flex gap-2 ml-auto">
        <button
          @click="cancelEditing"
          class="h-11 sm:h-9 px-3 rounded-lg border border-border bg-card hover:bg-muted text-sm font-medium text-foreground transition"
        >
          <i class="fas fa-times mr-1 text-xs"></i> Cancel
        </button>
        <button
          @click="saveChanges"
          class="h-11 sm:h-9 px-3.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold shadow-sm transition"
        >
          <i class="fas fa-check mr-1 text-xs"></i> Save Changes
        </button>
      </div>
    </div>

    <!-- Toast Component -->
    <ToastNotification
      :show="toast.show"
      :type="toast.type"
      :title="toast.title"
      :message="toast.message"
      @close="toast.show = false"
    />
  </div>
</template>

<script>
  import { ref, reactive, computed, onMounted } from 'vue';
  import apiClient from '@/services/api';
  import ToastNotification from '@/components/Toast.vue';
  import ExperienceStoriesList from '@/components/ExperienceStoriesList.vue';
  import authStore from '@/store/auth';
  import { slugify } from '@/utils/slug';
  import { useTheme } from '@/composables/useTheme';
  import { sourceLogo } from '@/utils/sourceLogos';

  const HOURS_PER_WEEK_OPTIONS = [
    { value: 'more_than_30', label: 'More than 30 hrs/week' },
    { value: 'less_than_30', label: 'Less than 30 hrs/week' },
    { value: 'as_needed', label: 'As needed' },
  ];

  const ENGLISH_LEVEL_OPTIONS = [
    { value: 'basic', label: 'Basic' },
    { value: 'conversational', label: 'Conversational' },
    { value: 'fluent', label: 'Fluent' },
    { value: 'native', label: 'Native or bilingual' },
  ];

  const labelFor = (options, value) => options.find((o) => o.value === value)?.label || '';

  // The API stores dates; the month inputs work in 'YYYY-MM'.
  const toMonth = (value) => (value ? String(value).slice(0, 7) : '');
  const toDate = (month) => (month ? `${month}-01` : null);

  const formatMonth = (value) => {
    if (!value) return '';
    const [year, month] = String(value).split('-').map(Number);
    return new Date(year, (month || 1) - 1, 1).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  };

  const formatDateRange = (start, end) => `${formatMonth(start)} – ${end ? formatMonth(end) : 'Present'}`;

  export default {
    name: 'ProfilePage',
    components: {
      ToastNotification,
      ExperienceStoriesList
    },
    setup() {
      // Profile data
      const profile = ref({
        id: null,
        // Basic Information
        title: '',
        about: '',
        years_of_experience: '',
        location: '',
        open_to_remote: false,
        willing_to_relocate: false,
        hourly_rate: null,
        available_for: '',
        profile_picture: '',

        // Resume
        resume: null,
        resume_url: '',

        // Skills
        skills: [],

        // Portfolio & Links
        website: '',
        linkedin: '',
        github: '',
        experience: [],
        projects: [],

        // Upwork
        upwork_profile_url: '',
        hours_per_week: '',
        english_level: '',
        open_to_contract_to_hire: false,
        upwork_stats: {},

        // Job Preferences
        job_preferences: {
          job_types: [],
          salary_min: null,
          salary_max: null,
          work_arrangement: 'flexible',
          availability: 'immediately',
          custom_availability: ''
        }
      });

      // User info
      const user = ref({
        id: null,
        username: '',
        firstName: '',
        lastName: '',
        email: ''
      });

      // Editing state
      const isEditing = ref(false);
      const editedProfile = reactive({
        id: null,
        title: '',
        about: '',
        years_of_experience: '',
        location: '',
        open_to_remote: false,
        willing_to_relocate: false,
        hourly_rate: null,
        available_for: '',
        resume: null,
        resume_url: '',
        skills: [],
        website: '',
        linkedin: '',
        github: '',
        experience: [],
        projects: [],
        upwork_profile_url: '',
        hours_per_week: '',
        english_level: '',
        open_to_contract_to_hire: false,
        job_preferences: {
          job_types: [],
          salary_min: null,
          salary_max: null,
          work_arrangement: 'flexible',
          availability: 'immediately',
          custom_availability: ''
        },
        newResume: null
      });
      const openSection = ref(null);
      const fileInput = ref(null);
      const skillInput = ref('');
      const resumeParsing = ref(false);
      const resumeScanFailed = ref(false);
      const resumeAutofillFields = ref([]);

      // Public profile link state
      const copied = ref(false);
      const privacySettings = ref({
        profile_visibility: 'public',
        allow_recruiter_contact: true,
        hide_current_employer: false
      });

      // Popular skills - would come from API in production
      const popularSkills = [
        'JavaScript', 'React', 'Python', 'Node.js', 'SQL',
        'Project Management', 'Marketing', 'Sales', 'Data Analysis',
        'UX/UI Design', 'Communication', 'Leadership'
      ];

      // Computed properties
      const userInitials = computed(() => {
        if (user.value.firstName && user.value.lastName) {
          return `${user.value.firstName[0]}${user.value.lastName[0]}`.toUpperCase();
        } else if (user.value.username) {
          return user.value.username.substring(0, 2).toUpperCase();
        } else {
          return 'JS'; // Default: JobSeeker
        }
      });

      const hasResume = computed(() => {
        return profile.value.resume || profile.value.resume_url;
      });

      const publicProfileUrl = computed(() => {
        if (profile.value?.id) {
          const first = (user.value?.firstName || user.value?.first_name || '').toString().trim();
          const last = (user.value?.lastName || user.value?.last_name || '').toString().trim();
          const username = (user.value?.username || '').toString().trim();
          const fullName = `${first} ${last}`.trim();
          const slug = slugify(fullName) || slugify(username);
          const id = profile.value.id;
          return slug
            ? `${window.location.origin}/jobseekers/info/${slug}-${id}/`
            : `${window.location.origin}/jobseekers/info/${id}/`;
        }
        if (user.value?.id) {
          return `${window.location.origin}/jobseekers/${user.value.id}`;
        }
        return '';
      });

      const canShareProfile = computed(() => {
        return privacySettings.value?.profile_visibility === 'public';
      });

      const { theme } = useTheme();
      const upworkLogo = computed(() => sourceLogo('upwork', theme.value));

      const upworkStatList = computed(() => {
        const stats = profile.value.upwork_stats || {};
        const list = [];
        if (stats.job_success != null) list.push({ label: 'Job Success', value: `${stats.job_success}%` });
        if (stats.total_earnings) list.push({ label: 'Total earnings', value: stats.total_earnings });
        if (stats.total_jobs != null) list.push({ label: 'Total jobs', value: stats.total_jobs });
        if (stats.total_hours != null) list.push({ label: 'Total hours', value: stats.total_hours });
        if (stats.rating != null) list.push({ label: 'Client rating', value: `${stats.rating} / 5` });
        return list;
      });
      const hasUpworkStats = computed(() => upworkStatList.value.length > 0);

      const workDetailRows = computed(() => {
        const p = profile.value;
        const rate = p.hourly_rate != null && p.hourly_rate !== '' ? `$${Number(p.hourly_rate).toFixed(2)}/hr` : '';
        return [
          { icon: 'fa-map-marker-alt', value: p.location, empty: 'Location not specified' },
          { icon: 'fa-dollar-sign', value: rate, empty: 'Hourly rate not specified' },
          { icon: 'fa-clock', value: labelFor(HOURS_PER_WEEK_OPTIONS, p.hours_per_week), empty: 'Hours per week not specified' },
          { icon: 'fa-language', value: p.english_level ? `English: ${labelFor(ENGLISH_LEVEL_OPTIONS, p.english_level)}` : '', empty: 'English level not specified' },
          { icon: 'fa-handshake', value: p.open_to_contract_to_hire ? 'Open to contract-to-hire' : '', empty: 'Not open to contract-to-hire' },
        ];
      });

      // Profile completeness computed property
      const completeness = computed(() => {
        const p = profile.value;
        const checks = [
          !!p.title,
          !!p.about,
          p.skills && p.skills.length > 0,
          !!(p.resume || p.resume_url),
          !!p.location,
          p.hourly_rate != null && p.hourly_rate !== '',
          p.experience && p.experience.length > 0,
          p.projects && p.projects.length > 0,
        ];
        const filled = checks.filter(Boolean).length;
        return Math.round((filled / checks.length) * 100);
      });

      const copyProfileUrl = async () => {
        if (!canShareProfile.value || !publicProfileUrl.value) return;
        try {
          await navigator.clipboard.writeText(publicProfileUrl.value);
          copied.value = true;
          setTimeout(() => {
            copied.value = false;
          }, 3000);
        } catch (err) {
          console.error('Could not copy text: ', err);
        }
      };

      const fetchPrivacySettings = async () => {
        try {
          const res = await apiClient.get('/settings/privacy/');
          privacySettings.value = res.data;
        } catch (err) {
          console.error('Error fetching privacy settings:', err);
        }
      };

      // Load profile data
      const fetchProfileData = async () => {
        try {
          // Fetch user data
          const userResponse = await apiClient.get('/auth/user/');
          user.value = userResponse.data;

          // Fetch profile data
          const profileResponse = await apiClient.get('/jobseeker/profile/');

          // Update profile with response data, ensuring all fields are present
          profile.value = {
            id: profileResponse.data.id,
            title: profileResponse.data.title || '',
            about: profileResponse.data.about || '',
            years_of_experience: profileResponse.data.years_of_experience || '',
            location: profileResponse.data.location || '',
            open_to_remote: profileResponse.data.open_to_remote || false,
            willing_to_relocate: profileResponse.data.willing_to_relocate || false,
            hourly_rate: profileResponse.data.hourly_rate || null,
            available_for: profileResponse.data.available_for || '',
            resume: profileResponse.data.resume || null,
            resume_url: profileResponse.data.resume_url || '',
            skills: profileResponse.data.skills || [],
            website: profileResponse.data.website || '',
            linkedin: profileResponse.data.linkedin || '',
            github: profileResponse.data.github || '',
            profile_picture: profileResponse.data.profile_picture || '',
            experience: profileResponse.data.experience || [],
            projects: profileResponse.data.projects || [],
            upwork_profile_url: profileResponse.data.upwork_profile_url || '',
            hours_per_week: profileResponse.data.hours_per_week || '',
            english_level: profileResponse.data.english_level || '',
            open_to_contract_to_hire: !!profileResponse.data.open_to_contract_to_hire,
            upwork_stats: profileResponse.data.upwork_stats || {},
            job_preferences: profileResponse.data.job_preferences || {
              job_types: [],
              salary_min: null,
              salary_max: null,
              work_arrangement: 'flexible',
              availability: 'immediately',
              custom_availability: ''
            }
          };

        } catch (error) {
          console.error('Error fetching profile data:', error);
          if (error.response?.data) {
            console.error('Server error details:', error.response.data);
          }
        }
      };

      // Start editing profile
      const startEditing = () => {
        // Create a deep copy of the profile to edit
        editedProfile.title = profile.value.title || '';
        editedProfile.about = profile.value.about || '';
        editedProfile.years_of_experience = profile.value.years_of_experience || '';
        editedProfile.location = profile.value.location || '';
        editedProfile.open_to_remote = profile.value.open_to_remote || false;
        editedProfile.willing_to_relocate = profile.value.willing_to_relocate || false;
        editedProfile.hourly_rate = profile.value.hourly_rate ?? null;
        editedProfile.available_for = profile.value.available_for || '';
        editedProfile.resume = profile.value.resume || null;
        editedProfile.resume_url = profile.value.resume_url || '';
        editedProfile.skills = [...(profile.value.skills || [])];
        editedProfile.website = profile.value.website || '';
        editedProfile.linkedin = profile.value.linkedin || '';
        editedProfile.github = profile.value.github || '';
        editedProfile.experience = profile.value.experience ? profile.value.experience.map(e => ({...e})) : [];
        editedProfile.projects = (profile.value.projects || []).map(p => ({
          title: p.title || '', description: p.description || '', url: p.url || '',
          role: p.role || '', tools: [...(p.tools || [])], highlights: p.highlights || '',
        }));
        editedProfile.upwork_profile_url = profile.value.upwork_profile_url || '';
        editedProfile.hours_per_week = profile.value.hours_per_week || '';
        editedProfile.english_level = profile.value.english_level || '';
        editedProfile.open_to_contract_to_hire = !!profile.value.open_to_contract_to_hire;
        editedProfile.job_preferences = {
          job_types: [...(profile.value.job_preferences?.job_types || [])],
          salary_min: profile.value.job_preferences?.salary_min || null,
          salary_max: profile.value.job_preferences?.salary_max || null,
          work_arrangement: profile.value.job_preferences?.work_arrangement || 'flexible',
          availability: profile.value.job_preferences?.availability || 'immediately',
          custom_availability: profile.value.job_preferences?.custom_availability || ''
        };
        editedProfile.newResume = null;

        isEditing.value = true;
        openSection.value = null;
      };

      // Cancel editing
      const cancelEditing = () => {
        isEditing.value = false;
        openSection.value = null;

        // Reset any file upload
        editedProfile.newResume = null;
        if (fileInput.value) {
          fileInput.value.value = '';
        }
      };

      // Add toast state
      const toast = reactive({
        show: false,
        type: 'success',
        title: '',
        message: ''
      });

      const showToast = (type, title, message) => {
        toast.type = type;
        toast.title = title;
        toast.message = message;
        toast.show = true;
        setTimeout(() => {
          toast.show = false;
        }, 5000);
      };

      // Handle image error
      const handleImageError = (event) => {
        event.target.style.display = 'none';
        // Show fallback avatar if image fails to load
      };

      // Save changes
      const saveChanges = async () => {
        try {
          // Create a copy of the edited profile data
          const profileData = {
            title: editedProfile.title || '',
            about: editedProfile.about || '',
            years_of_experience: editedProfile.years_of_experience || '',
            location: editedProfile.location || '',
            open_to_remote: editedProfile.open_to_remote || false,
            willing_to_relocate: editedProfile.willing_to_relocate || false,
            hourly_rate: editedProfile.hourly_rate ?? null,
            available_for: editedProfile.available_for || '',
            website: editedProfile.website || '',
            linkedin: editedProfile.linkedin || '',
            github: editedProfile.github || '',
            resume_url: editedProfile.resume_url || '',
            skills: editedProfile.skills || [],
            experience: editedProfile.experience.map(entry => ({
              title: entry.title || '',
              company: entry.company || '',
              description: entry.description || '',
              start_date: entry.start_date || null,
              end_date: entry.end_date || null,
              tools: entry.tools || [],
              highlights: (entry.highlights || '').trim()
            })) || [],
            projects: editedProfile.projects
              .filter(project => (project.title || '').trim())
              .map(project => ({
                title: project.title.trim(),
                description: (project.description || '').trim(),
                url: (project.url || '').trim(),
                role: (project.role || '').trim(),
                tools: project.tools || [],
                highlights: (project.highlights || '').trim()
              })),
            upwork_profile_url: (editedProfile.upwork_profile_url || '').trim(),
            hours_per_week: editedProfile.hours_per_week || '',
            english_level: editedProfile.english_level || '',
            open_to_contract_to_hire: !!editedProfile.open_to_contract_to_hire,
            job_preferences: {
              job_types: editedProfile.job_preferences?.job_types || [],
              salary_min: editedProfile.job_preferences?.salary_min || null,
              salary_max: editedProfile.job_preferences?.salary_max || null,
              work_arrangement: editedProfile.job_preferences?.work_arrangement || 'flexible',
              availability: editedProfile.job_preferences?.availability || 'immediately',
              custom_availability: editedProfile.job_preferences?.custom_availability || ''
            }
          };

          // Determine if we need FormData (for file uploads)
          let data;
          const hasFileUpload = editedProfile.newResume !== null;

          if (hasFileUpload) {
            data = new FormData();

            // Add the new resume file
            data.append('resume_file', editedProfile.newResume);

            // Add all other profile fields as JSON strings
            Object.keys(profileData).forEach(key => {
              if (key === 'job_preferences') {
                // For job_preferences, we need to handle it specially
                // The backend expects job_preference (singular) but we're using job_preferences (plural)
                const jobPreferenceData = {
                  job_types: profileData.job_preferences.job_types || [],
                  salary_min: profileData.job_preferences.salary_min || null,
                  salary_max: profileData.job_preferences.salary_max || null,
                  work_arrangement: profileData.job_preferences.work_arrangement || 'flexible',
                  availability: profileData.job_preferences.availability || 'immediately',
                  custom_availability: profileData.job_preferences.custom_availability || ''
                };
                data.append('job_preference', JSON.stringify(jobPreferenceData));
              } else if (key === 'experience' || key === 'skills' || key === 'projects') {
                data.append(key, JSON.stringify(profileData[key]));
              } else {
                // Convert boolean values to strings
                if (typeof profileData[key] === 'boolean') {
                  data.append(key, profileData[key].toString());
                } else {
                  data.append(key, profileData[key] || '');
                }
              }
            });
          } else {
            data = profileData;
          }


          // Save profile
          const response = await apiClient.put('/jobseeker/profile/', data, {
            headers: hasFileUpload ? {
              'Content-Type': 'multipart/form-data'
            } : {
              'Content-Type': 'application/json'
            }
          });

          // Update profile with response data
          profile.value = response.data;

          // Exit edit mode
          isEditing.value = false;
          openSection.value = null;

          // Show success message
          showToast(
            'success',
            'Profile Updated',
            'Your profile has been updated successfully!'
          );

          // Refresh onboarding status and user data
          await apiClient.post('/auth/check-onboarding-status/');
          const userResponse = await apiClient.get('/auth/user/');
          if (userResponse.data) {
            user.value = userResponse.data;
            // If you use a global auth store, also update it here:
            if (typeof authStore !== 'undefined' && authStore.setAuth) {
              authStore.setAuth(true, userResponse.data);
            }
          }

          // Refresh profile data
          await fetchProfileData();
        } catch (error) {
          console.error('Error saving profile:', error);
          let errorMessage = 'There was an error updating your profile. Please try again.';
          if (error.response?.data) {
            if (typeof error.response.data === 'object') {
              errorMessage = Object.entries(error.response.data)
                .map(([key, value]) => `${key}: ${value}`)
                .join('\n');
            } else {
              errorMessage = error.response.data;
            }
          }
          showToast('error', 'Error', errorMessage);
        }
      };

      // Resume download
      const downloadResume = async () => {
        try {
          const response = await apiClient.get('/jobseeker/resume/download/', {
            responseType: 'blob'
          });

          // Create a blob URL and trigger download
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = profile.value.resume?.name || 'resume';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error('Error downloading resume:', error);
          alert('Error downloading resume. Please try again.');
        }
      };

      // Remove resume
      const removeResume = () => {
        editedProfile.resume = null;
        editedProfile.newResume = null;
        resumeParsing.value = false;
        resumeScanFailed.value = false;
        resumeAutofillFields.value = [];
        if (fileInput.value) {
          fileInput.value.value = '';
        }
      };

      const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      const pollResumeParseTask = async (taskId) => {
        const maxAttempts = 45;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          const res = await apiClient.get('/resume-parse/status/', { params: { task_id: taskId } });
          const payload = res.data || {};
          if (payload.status === 'completed') return payload.data || {};
          if (payload.status === 'failed') throw new Error(payload.detail || 'Resume AI parsing failed.');
          await sleep(2000);
        }
        throw new Error('Resume parsing timed out.');
      };

      const scanAndApplyResume = async (file) => {
        resumeScanFailed.value = false;
        resumeAutofillFields.value = [];
        resumeParsing.value = true;
        try {
          const formData = new FormData();
          formData.append('resume', file);
          const response = await apiClient.post('/resume-parse/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const taskId = response?.data?.task_id;
          if (!taskId) throw new Error('Parsing task not created.');
          const data = await pollResumeParseTask(taskId);

          const filled = [];
          if (data.title && !editedProfile.title) { editedProfile.title = data.title; filled.push('Title'); }
          if (data.summary && !editedProfile.about) { editedProfile.about = data.summary; filled.push('About / Summary'); }
          if (data.experience && !editedProfile.years_of_experience) { editedProfile.years_of_experience = data.experience; filled.push('Years of experience'); }
          if (data.location && !editedProfile.location) { editedProfile.location = data.location; filled.push('Location'); }
          if (data.website && !editedProfile.website) { editedProfile.website = data.website; filled.push('Website'); }
          if (data.linkedin && !editedProfile.linkedin) { editedProfile.linkedin = data.linkedin; filled.push('LinkedIn'); }
          if (data.github && !editedProfile.github) { editedProfile.github = data.github; filled.push('GitHub'); }
          if (data.skills && data.skills.length > 0) {
            const added = [];
            data.skills.forEach((s) => { if (s && !editedProfile.skills.includes(s)) { editedProfile.skills.push(s); added.push(s); } });
            if (added.length) filled.push(`${added.length} skill${added.length > 1 ? 's' : ''}`);
          }
          if (data.work_history && data.work_history.length > 0) {
            const addedEntries = [];
            data.work_history.forEach((e) => {
              const title = (e.title || e.name || '').trim();
              const company = (e.company || '').trim();
              const description = (e.description || '').trim();
              if (!title && !company && !description) return;
              const dup = editedProfile.experience.some(
                (x) => (x.title || '').trim() === title && (x.company || '').trim() === company,
              );
              if (!dup) {
                editedProfile.experience.push({
                  title: title || 'Role', company, description,
                  start_date: toDate(e.start_date), end_date: toDate(e.end_date),
                  tools: e.tools || [], highlights: e.highlights || '',
                });
                addedEntries.push(title);
              }
            });
            if (addedEntries.length) filled.push(`${addedEntries.length} work experience entr${addedEntries.length > 1 ? 'ies' : 'y'}`);
          }
          if (data.portfolio && data.portfolio.length > 0) {
            let addedProjects = 0;
            data.portfolio.forEach((p) => {
              const title = (p.title || '').trim();
              if (!title || editedProfile.projects.some((x) => (x.title || '').trim() === title)) return;
              editedProfile.projects.push({
                title, description: p.description || '', url: p.url || '',
                role: p.role || '', tools: p.tools || [], highlights: p.highlights || '',
              });
              addedProjects += 1;
            });
            if (addedProjects) filled.push(`${addedProjects} project${addedProjects > 1 ? 's' : ''}`);
          }
          if (data.salary_min && !editedProfile.job_preferences.salary_min) { editedProfile.job_preferences.salary_min = data.salary_min; }
          if (data.salary_max && !editedProfile.job_preferences.salary_max) { editedProfile.job_preferences.salary_max = data.salary_max; }

          resumeAutofillFields.value = filled;
          if (filled.length > 0) {
            showToast('success', 'Resume scanned', `Pre-filled ${filled.join(', ')} from your resume.`);
          }
        } catch (err) {
          console.error('Resume scan error:', err);
          resumeScanFailed.value = true;
        } finally {
          resumeParsing.value = false;
        }
      };

      // Resume upload handling
      const handleResumeUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
          if (validateFile(file)) {
            editedProfile.newResume = file;
            resumeAutofillFields.value = [];
            resumeScanFailed.value = false;
            scanAndApplyResume(file);
          } else {
            alert("Please upload a PDF, DOC, or DOCX file under 5MB.");
          }
        }
      };

      const validateFile = (file) => {
        // Check file type
        const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!validTypes.includes(file.type)) {
          return false;
        }

        // Check file size (5MB max)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
          return false;
        }

        return true;
      };

      // Skills methods
      const addSkill = () => {
        const skill = skillInput.value.trim();
        if (skill && !editedProfile.skills.includes(skill)) {
          editedProfile.skills.push(skill);
          skillInput.value = '';
        }
      };

      const removeSkill = (index) => {
        editedProfile.skills.splice(index, 1);
      };

      const addPopularSkill = (skill) => {
        if (!editedProfile.skills.includes(skill)) {
          editedProfile.skills.push(skill);
        }
      };

      // Work experience methods
      const addExperience = () => {
        editedProfile.experience.push({
          title: '',
          company: '',
          description: '',
          tools: [],
          highlights: ''
        });
      };

      const removeExperience = (index) => {
        editedProfile.experience.splice(index, 1);
      };

      const addProject = () => {
        editedProfile.projects.push({ title: '', description: '', url: '', role: '', tools: [], highlights: '' });
      };

      // "Asana, HubSpot ,asana" -> ['Asana', 'HubSpot']
      const parseTools = (text) => {
        const seen = new Set();
        return (text || '').split(',').map((t) => t.trim()).filter((t) => {
          if (!t || seen.has(t.toLowerCase())) return false;
          seen.add(t.toLowerCase());
          return true;
        }).slice(0, 30);
      };

      const removeProject = (index) => {
        editedProfile.projects.splice(index, 1);
      };

      const handleExperienceChange = (index, field, value) => {
        editedProfile.experience[index][field] = value;
      };

      // Helper functions for display
      const getExperienceText = (experience) => {
        const experienceMap = {
          '0-1': 'Less than 1 year of experience',
          '1-3': '1-3 years of experience',
          '3-5': '3-5 years of experience',
          '5-10': '5-10 years of experience',
          '10+': '10+ years of experience'
        };

        return experienceMap[experience] || 'Experience not specified';
      };

      const formatJobType = (type) => {
        const typeMap = {
          'full-time': 'Full-time',
          'part-time': 'Part-time',
          'contract': 'Contract',
          'freelance': 'Freelance',
          'internship': 'Internship'
        };

        return typeMap[type] || type;
      };

      const formatSalaryRange = (min, max) => {
        if (min && max) {
          return `${min.toLocaleString()} - ${max.toLocaleString()} monthly`;
        } else if (min) {
          return `${min.toLocaleString()}+ monthly`;
        } else if (max) {
          return `Up to ${max.toLocaleString()} monthly`;
        }
        return 'Not specified';
      };

      const formatWorkArrangement = (arrangement) => {
        const arrangementMap = {
          'on-site': 'On-site',
          'remote': 'Remote',
          'hybrid': 'Hybrid',
          'flexible': 'Flexible'
        };

        return arrangementMap[arrangement] || arrangement;
      };

      const formatAvailability = (availability, customAvailability) => {
        const availabilityMap = {
          'immediately': 'Available immediately',
          '2-weeks': 'Available with 2 weeks notice',
          '1-month': 'Available with 1 month notice',
          'custom': customAvailability || 'Custom availability'
        };

        return availabilityMap[availability] || availability;
      };

      // Resume methods
      const getResumeIcon = (filename) => {
        if (!filename) return 'fas fa-file-alt';
        if (filename.toLowerCase().endsWith('.pdf')) {
          return 'fas fa-file-pdf text-red-500';
        } else if (filename.toLowerCase().endsWith('.doc') || filename.toLowerCase().endsWith('.docx')) {
          return 'fas fa-file-word text-blue-500';
        }
        return 'fas fa-file-alt';
      };

      const getResumeViewUrl = () => {
        return `/api/jobseeker/resume/view/`;
      };

      const oljIntegration = reactive({ connected: false, id_proof: null });

      onMounted(() => {
        fetchProfileData();
        fetchPrivacySettings();
        fetchOljIntegration();
      });

      // Fetch OLJ integration status and ID Proof
      const fetchOljIntegration = async () => {
        try {
          const res = await apiClient.get('/settings/integrations/');
          if ('olj_email' in res.data) {
            oljIntegration.connected = !!res.data.olj_email;
            oljIntegration.id_proof = res.data.olj_id_proof !== undefined ? res.data.olj_id_proof : null;
          } else if (res.data.integrations && Array.isArray(res.data.integrations)) {
            const olj = res.data.integrations.find(i => i.type === 'olj');
            oljIntegration.connected = !!olj;
            oljIntegration.id_proof = olj && olj.olj_id_proof !== undefined ? olj.olj_id_proof : null;
          } else {
            oljIntegration.connected = false;
            oljIntegration.id_proof = null;
          }
        } catch (e) {
          oljIntegration.connected = false;
          oljIntegration.id_proof = null;
        }
      };

      const makingPublic = ref(false);
      const makeProfilePublic = async () => {
        if (makingPublic.value) return;
        makingPublic.value = true;
        try {
          const res = await apiClient.patch('/settings/privacy/', {
            profile_visibility: 'public'
          });
          privacySettings.value = {
            ...privacySettings.value,
            ...res.data,
            profile_visibility: 'public'
          };
        } catch (err) {
          console.error('Error updating privacy settings:', err);
          showToast('error', 'Error', 'Unable to set your profile to public. Please try again.');
        } finally {
          makingPublic.value = false;
        }
      };

      return {
        profile,
        user,
        userInitials,
        isEditing,
        editedProfile,
        openSection,
        fileInput,
        skillInput,
        copied,
        privacySettings,
        hasResume,
        publicProfileUrl,
        canShareProfile,
        completeness,
        fetchPrivacySettings,
        fetchProfileData,
        startEditing,
        cancelEditing,
        saveChanges,
        downloadResume,
        removeResume,
        handleResumeUpload,
        validateFile,
        addSkill,
        removeSkill,
        addPopularSkill,
        addExperience,
        removeExperience,
        addProject,
        removeProject,
        parseTools,
        handleExperienceChange,
        upworkLogo,
        upworkStatList,
        hasUpworkStats,
        workDetailRows,
        HOURS_PER_WEEK_OPTIONS,
        ENGLISH_LEVEL_OPTIONS,
        toMonth,
        toDate,
        formatDateRange,
        makeProfilePublic,
        copyProfileUrl,
        showToast,
        handleImageError,
        // Helper functions
        getExperienceText,
        formatJobType,
        formatSalaryRange,
        formatWorkArrangement,
        formatAvailability,
        getResumeIcon,
        getResumeViewUrl,
        popularSkills,
        toast,
        oljIntegration,
        makingPublic,
        resumeParsing,
        resumeScanFailed,
        resumeAutofillFields,
      };
    }
  };
  </script>

<style scoped>
/* No custom CSS required — all styling via Tailwind utility classes */
</style>
